import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Typography } from '@tempmee/react';
import Styles from './ListPageHeader.module.scss';

/**
 * The header for an admin list page: title on the left, actions on the right,
 * and an optional second row for counts or filters.
 *
 * The six list pages each grew their own version of this — centred titles,
 * absolutely positioned buttons, totals as stray `<h4>` blocks, and one page
 * with no title at all. The parts that genuinely differ are the three slots.
 *
 * `actions` and `meta` are slots rather than typed props because what goes in
 * them varies too much to model: a "Post Job" button on one page, a reload
 * icon on another, one total here and two there.
 */
export const ListPageHeader = ({ title, actions, meta, isLoading }) => (
  <header className={Styles.header}>
    <div className={Styles.titleRow}>
      <div className={Styles.title}>
        <Typography as="h1" size="heading_3" bold>
          {title}
        </Typography>

        {/* Sized to the heading rather than reusing the shared Spinner: this
            marks a refresh of a list that is already on screen, where that one
            stands in for a page that is not. */}
        {isLoading ? <CircularProgress size={20} /> : null}
      </div>

      {actions ? <div className={Styles.actions}>{actions}</div> : null}
    </div>

    {meta ? <div className={Styles.meta}>{meta}</div> : null}
  </header>
);

ListPageHeader.propTypes = {
  title: PropTypes.node.isRequired,
  /** Right side of the title row — a create button, a reload control. */
  actions: PropTypes.node,
  /** Second row — totals, filters. */
  meta: PropTypes.node,
  /** Shows a small spinner beside the title while the list refreshes. */
  isLoading: PropTypes.bool,
};

ListPageHeader.defaultProps = {
  actions: null,
  meta: null,
  isLoading: false,
};

export default ListPageHeader;
